import { useEffect, useState, useContext, useRef } from "react";
import scheduleBasedRequest from "../../request/scheduleBasedRequest";
import { CombinedContext } from "../../context/combinedContext";
import "./bookingModal.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useNavigate } from "react-router-dom";
import translations from "../../translations/translations";
import BookingModalSkeleton from "./bookingModalSkeleton/bookingModalSkeleton";
import { ArrowLeft, ChevronLeft, ChevronRight, Users } from "lucide-react";
import CustomSelect from "../common/customSelect/customSelect";
import BannerMessage from "../common/bannerMessage/bannerMessage";
import RSFNewAutocomplete from "../../utils/addressAutofill/addressAutocomplete";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import PhoneInput from "../common/phoneInput/phoneInput";
import StaffDropdown from "../common/StaffDropdown/StaffDropdown";

dayjs.extend(utc);
dayjs.extend(timezone);

const BookingModal = ({
    open,
    onClose,
    availabilityId,
    shopData,
    initialstaffId,
    initialServiceId,
    initialServices,
    mode,
    existingBooking,
}) => {
    const { selectedstaff, setSelectedstaff, userData, language } = useContext(CombinedContext);
    const [date, setDate] = useState(dayjs().tz("America/Santo_Domingo").format("YYYY-MM-DD"));
    const [selTime, setSelTime] = useState("");
    const [timeData, setTimeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [dayIndex, setDayIndex] = useState(0);
    const [startDate, setStartDate] = useState(dayjs().tz("America/Santo_Domingo").format("YYYY-MM-DD"));
    const [toast, setToast] = useState(null);
    const [customerName, setCustomerName] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [selectedServices, setSelectedServices] = useState([]);
    const [timePeriod, setTimePeriod] = useState("Morning");
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const [validationError, setValidationError] = useState("");
    const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);
    const [homeAddress, setHomeAddress] = useState({
        address: "",
        city: "",
        country: "",
        lat: null,
        lng: null,
    });

    const [homePhone, setHomePhone] = useState("");

    console.log(existingBooking)

    const slotsRef = useRef(null);

    const navigate = useNavigate();

    const isLoggedIn = userData;
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    }, []);

    useEffect(() => {
        if (!open) return;

		if (initialServices?.length) {
			const seeded = initialServices.map((s) => shopData.services.find((x) => x.serviceId === s.serviceId)).filter(Boolean);
			if (seeded.length) setSelectedServices(seeded);
		} else if (initialServiceId) {
			const s = shopData.services.find((x) => x.serviceId === initialServiceId);
			if (s) setSelectedServices([s]);
		}
        if (initialstaffId) {
            setSelectedstaff(initialstaffId);
        } else {
            // 👇 Force "Any staff" by default
            setSelectedstaff("");
        }

        setSelTime("");
        setDate(dayjs().tz("America/Santo_Domingo").format("YYYY-MM-DD"));
        setStartDate(dayjs().tz("America/Santo_Domingo").format("YYYY-MM-DD"));
        setDayIndex(0);
        setStaffDropdownOpen(false);
    }, [open]);

    useEffect(() => {
        if (existingBooking && !isLoggedIn) {
            setCustomerName(existingBooking.customerName || "");
            setCustomerEmail(existingBooking.customerEmail || "");
            setHomeAddress({
                address: existingBooking.address || "",
                city: existingBooking.city || "",
                country: existingBooking.country || "",
                lat: existingBooking.lat || null,
                lng: existingBooking.lng || null,
            });
            setHomePhone(existingBooking.phone || "");
        }
    }, [existingBooking, isLoggedIn]);

    useEffect(() => {
        if (!open) return;

        if (initialServiceId) {
            const s = shopData.services.find((x) => x.serviceId === initialServiceId);
            if (s) setSelectedServices([s]);
        }

        if (initialstaffId) setSelectedstaff(initialstaffId);

        setSelTime("");
        setDate(dayjs().tz("America/Santo_Domingo").format("YYYY-MM-DD"));
        setStartDate(dayjs().tz("America/Santo_Domingo").format("YYYY-MM-DD"));
        setDayIndex(0);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev || "";
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const startStr = startDate;

        async function loadAvailability() {
            try {
                if (!availabilityId) throw new Error("Missing availabilityId");
                setLoading(true);

                const data = await scheduleBasedRequest.getAvailability(availabilityId, {
                    startDate: startStr,
                });

                setTimeData(data);
                if (data?.week && date) {
                    const weekEntries = Object.entries(data.week);

                    // find first open/busy day with slots
                    const nextOpen = weekEntries.find(([_, info]) => info.totalSlots > 0);

                    if (nextOpen) {
                        const [nextDate] = nextOpen;
                        // const nextDateObj = new Date(nextDate);

                        // if current date is closed/full, auto-move
                        if (!data.week[date]?.totalSlots) {
                            setDate(nextDate);
                            setDayIndex(weekEntries.findIndex(([d]) => d === nextDate));
                        }
                    }
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }

        loadAvailability();
    }, [open, availabilityId, startDate]);

    function filterSlotsByDuration(slots, totalDuration) {
        if (!totalDuration) return slots;

        const neededSlots = Math.ceil(totalDuration / 15);

        return slots.filter((slot, index) => {
            for (let i = 0; i < neededSlots; i++) {
                const next = slots[index + i];
                if (!next) return false;

                if (i > 0) {
                    const [hPrev, mPrev] = slots[index + i - 1].start.split(":").map(Number);
                    const [hNext, mNext] = next.start.split(":").map(Number);

                    const prevMinutes = hPrev * 60 + mPrev;
                    const nextMinutes = hNext * 60 + mNext;

                    if (nextMinutes - prevMinutes !== 15) {
                        return false;
                    }
                }
            }
            return true;
        });
    }

    const hasHomeService = selectedServices.some((s) => s.isHomeService === true);
    const hasInShopService = selectedServices.some((s) => s.isHomeService !== true);

    const currentDayStr = date;
    const dayData = timeData?.week?.[currentDayStr];
    const dayAvailability = dayData?.staffs || [];
    const selectedStaffData = selectedstaff ? dayAvailability.find((b) => b.staffId === selectedstaff) : null;

    const staffServiceIds = selectedStaffData?.service || [];
    const filteredServices = selectedstaff
        ? shopData.services.filter((svc) => staffServiceIds.includes(svc.serviceId))
        : shopData.services;
    const allSlotsUnique = Array.from(
        new Map(dayAvailability.flatMap((b) => (b.slots || []).map((s) => [s.start, s]))).values()
    );

    const totalPrice = selectedServices.reduce((sum, s) => sum + (s.price || 0), 0);
    const totalTime = selectedServices.reduce((sum, s) => sum + (s.duration || 0), 0);

    const maxTransitMinutes = hasHomeService ? Math.max(...selectedServices.map((s) => s.transitMinutes || 0)) : 0;

    const totalDurationWithTransit = hasHomeService ? totalTime + maxTransitMinutes : totalTime;

    function sortSlots(slots = []) {
        return [...slots].sort((a, b) => {
            const [hA, mA] = a.start.split(":").map(Number);
            const [hB, mB] = b.start.split(":").map(Number);
            return hA * 60 + mA - (hB * 60 + mB);
        });
    }

    function canStaffTakeHomeSlot(staff, slotStartStr) {
        if (!hasHomeService) return true;

        const tz = "America/Santo_Domingo";
        const start = dayjs.tz(`${date} ${slotStartStr}`, tz);

        if (!staff?.shiftStart) return true;

        const shiftStart = dayjs.tz(`${date} ${staff.shiftStart}`, tz);
        const minutesSinceShiftStart = start.diff(shiftStart, "minute");

        // first slot(s) near shift start are allowed even without prior slot
        if (minutesSinceShiftStart < maxTransitMinutes) {
            return true;
        }

        const neededStartStr = start.subtract(maxTransitMinutes, "minute").format("HH:mm");
        const staffSlotStarts = (staff.slots || []).map((s) => s.start);

        return staffSlotStarts.includes(neededStartStr);
    }

    function getValidSlotsForStaff(staff) {
        if (!staffCanHandleAllServices(staff)) return [];

        const staffSlots = sortSlots(staff.slots || []);
        const durationValid = filterSlotsByDuration(staffSlots, totalDurationWithTransit);

        return durationValid.filter((slot) => canStaffTakeHomeSlot(staff, slot.start));
    }

    const rawSlots = selectedstaff
        ? dayAvailability.find((b) => b.staffId === selectedstaff)?.slots || []
        : Array.from(
              new Map(
                  dayAvailability
                      .filter((b) => staffCanHandleAllServices(b))
                      .flatMap((staff) => {
                          const staffSlots = [...(staff.slots || [])].sort((a, b) => {
                              const [hA, mA] = a.start.split(":").map(Number);
                              const [hB, mB] = b.start.split(":").map(Number);
                              return hA * 60 + mA - (hB * 60 + mB);
                          });

                          return filterSlotsByDuration(staffSlots, totalDurationWithTransit);
                      })
                      .map((slot) => [slot.start, slot])
              ).values()
          );

    const sortedRawSlots = rawSlots.sort((a, b) => {
        const [hA, mA] = a.start.split(":").map(Number);
        const [hB, mB] = b.start.split(":").map(Number);
        return hA * 60 + mA - (hB * 60 + mB);
    });

    const slotsAfterDuration = filterSlotsByDuration(sortedRawSlots, totalDurationWithTransit);

    function slotRespectsTransit(slotStartStr) {
        if (!hasHomeService) return true;

        const tz = "America/Santo_Domingo";
        const start = dayjs.tz(`${date} ${slotStartStr}`, tz);

        // Determine staff shift start
        const staff = selectedstaff ? dayAvailability.find((b) => b.staffId === selectedstaff) : dayAvailability;

        if (!staff || !staff.shiftStart) return true;

        const shiftStart = dayjs.tz(`${date} ${staff.shiftStart}`, tz);

        // minutes since shift start
        const minutesSinceShiftStart = start.diff(shiftStart, "minute");

        // Allow slots inside transit window after shift start
        if (minutesSinceShiftStart < maxTransitMinutes) {
            return true;
        }

        // Otherwise enforce transit buffer
        const effectiveStart = start.subtract(maxTransitMinutes, "minute");
        const neededStartStr = effectiveStart.format("HH:mm");

        const staffSlots = rawSlots.map((s) => s.start);

        return staffSlots.includes(neededStartStr);
    }

    // const slotsToShow = slotsAfterDuration.filter((slot) => slotRespectsTransit(slot.start));
    const slotsToShow = selectedstaff
        ? getValidSlotsForStaff(dayAvailability.find((b) => b.staffId === selectedstaff) || {})
        : Array.from(
              new Map(
                  dayAvailability.flatMap((staff) => getValidSlotsForStaff(staff).map((slot) => [slot.start, slot]))
              ).values()
          ).sort((a, b) => {
              const [hA, mA] = a.start.split(":").map(Number);
              const [hB, mB] = b.start.split(":").map(Number);
              return hA * 60 + mA - (hB * 60 + mB);
          });

    // If at least one selected, lock the type
    const lockedType = hasHomeService ? "home" : hasInShopService ? "shop" : null;

    function canAddService(service) {
        if (!lockedType) return true;
        if (lockedType === "home") return service.isHomeService === true;
        return service.isHomeService !== true;
    }

    useEffect(() => {
        if (!selTime) return;

        const stillValid = slotsToShow.some((s) => s.start === selTime);

        if (!stillValid) {
            setSelTime("");
            setToast(translations[language].booking.toast.timeInvalidated);
        }
    }, [slotsToShow, selTime]);

    useEffect(() => {
        if (!hasHomeService) {
            setHomeAddress({
                address: "",
                city: "",
                country: "",
                lat: null,
                lng: null,
            });
            setHomePhone("");
        }
    }, [hasHomeService]);

    function staffCanHandleAllServices(staff) {
        if (!selectedServices.length) return true;

        const staffServices = staff.service || []; // array of serviceIds

        // ensure all selected serviceIds are included in staffServices
        return selectedServices.every((srv) => staffServices.includes(srv.serviceId));
    }

    function changeWeek(offset) {
        const newStart = dayjs(startDate)
            .add(offset * 7, "day")
            .format("YYYY-MM-DD");
        setStartDate(newStart);
        setDayIndex(0);
        setSelTime("");
    }

    function fmtDayLabel(iso) {
        return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
            weekday: "short",
            day: "numeric",
        });
    }

    function getTimePeriod(hour) {
        if (hour < 12) return "Morning";
        if (hour < 17) return "Afternoon";
        return "Evening";
    }

    const availablePeriods = new Set(slotsToShow.map((slot) => getTimePeriod(parseInt(slot.start.split(":")[0]))));

    async function confirmBooking() {
        setValidationError("");
        setConfirmLoading(true);

        if (!selTime && selectedServices.length === 0) {
            setConfirmLoading(false);
            setValidationError(translations[language].booking.toast.noServiceOrTime);
            return;
        }

        if (selectedServices.length === 0) {
            setConfirmLoading(false);
            setValidationError(translations[language].booking.toast.missingService);
            return;
        }

        if (!selTime) {
            setConfirmLoading(false);
            setValidationError(translations[language].booking.toast.missingTime);
            return;
        }

        if (!isLoggedIn && (!customerName || !customerEmail)) {
            setConfirmLoading(false);
            setValidationError(translations[language].booking.toast.missingGuest);
            return;
        }

        if (!homePhone) {
                setConfirmLoading(false);
                setValidationError(translations[language].booking.toast.missingGuest);
                return;
        }

        if (hasHomeService) {
            if (!homeAddress.address || !homeAddress.lat || !homeAddress.lng) {
                setValidationError(
                    language === "es"
                        ? "Por favor ingresa una dirección válida para el servicio a domicilio."
                        : "Please enter a valid address for the home service."
                );
                setConfirmLoading(false);
                return;
            }

            
        }

        const selectedstaffData = selectedstaff ? dayAvailability.find((b) => b.staffId === selectedstaff) : null;

        const availableStaffs = dayAvailability.filter((staff) => {
            if (!staffCanHandleAllServices(staff)) return false;

            const staffSlots = [...(staff.slots || [])].sort((a, b) => {
                const [hA, mA] = a.start.split(":").map(Number);
                const [hB, mB] = b.start.split(":").map(Number);
                return hA * 60 + mA - (hB * 60 + mB);
            });

            const validSlots = filterSlotsByDuration(staffSlots, totalDurationWithTransit).filter((slot) =>
                slotRespectsTransit(slot.start)
            );

            return validSlots.some((slot) => slot.start === selTime);
        });

        const fallbackstaffData =
            availableStaffs.length > 0 ? availableStaffs[Math.floor(Math.random() * availableStaffs.length)] : null;

        const chosenstaff = selectedstaffData?.staffId || fallbackstaffData?.staffId;

        const chosenstaffScheduleId = selectedstaffData?.scheduleId || fallbackstaffData?.scheduleId;

        const staffEmail = selectedstaffData?.email || fallbackstaffData?.email;

        const staffName = selectedstaffData
            ? `${selectedstaffData.firstName || ""} ${selectedstaffData.lastName || ""}`.trim()
            : fallbackstaffData
            ? `${fallbackstaffData.firstName || ""} ${fallbackstaffData.lastName || ""}`.trim()
            : "";

        if (!chosenstaff) {
            setConfirmLoading(false);
            setToast("No staff available for that time");
            return;
        }

        // ✅ Use shop timezone for everything
        const tz = "America/Santo_Domingo";
        const [hours, minutes] = selTime.split(":").map(Number);

        // Create the start/end in Santo Domingo time
        const startLocal = dayjs.tz(`${date} ${selTime}`, tz);

        const baseDuration = selectedServices.reduce((sum, s) => sum + (s.duration || 0), 0);

        const transitMinutes = hasHomeService ? Math.max(...selectedServices.map((s) => s.transitMinutes || 0)) : 0;

        const totalDuration = baseDuration + transitMinutes;

        const endLocal = startLocal.add(totalDuration, "minute");

        // ✅ Prevent booking in the past
        const now = dayjs().tz(tz);
        if (startLocal.isBefore(now)) {
            setConfirmLoading(false);
            setToast("You can’t book a time in the past.");
            return;
        }

        const payloadServices = selectedServices.map((s) => ({
            serviceId: s.serviceId,
            serviceNameEn: s.nameEn || s.name,
            serviceNameEs: s.nameEs || s.name,
            duration: s.duration || 0,
            isHomeService: s.isHomeService === true,
        }));

        const bookingPayload = {
            shopId: shopData.shopId,
            shopName: shopData.name,
            staffId: chosenstaff,
            scheduleId: chosenstaffScheduleId,
            staffsEmail: staffEmail,
            staffName: staffName,
            services: payloadServices,
            startTime: startLocal.tz("America/Santo_Domingo").format("YYYY-MM-DDTHH:mm:ss"),
            endTime: endLocal.tz("America/Santo_Domingo").format("YYYY-MM-DDTHH:mm:ss"),
            address: shopData.address || "",
            city: shopData.city || "",
            lat: shopData.lat || "",
            lng: shopData.lng || "",
            country: shopData.country || "",
            postalCode: shopData.postalCode || "",
            phone: shopData.phone || "",
            servicePhone: homePhone,
        };

        if (hasHomeService) {
            bookingPayload.isHomeService = true;
            bookingPayload.transitMinutes = maxTransitMinutes;

            bookingPayload.serviceAddress = homeAddress.address;
            bookingPayload.serviceCity = homeAddress.city;
            bookingPayload.serviceCountry = homeAddress.country;
            bookingPayload.servicePostalCode = homeAddress.postalCode || "";
            bookingPayload.serviceLat = homeAddress.lat;
            bookingPayload.serviceLng = homeAddress.lng;
        } else {
            bookingPayload.isHomeService = false;
            bookingPayload.transitMinutes = 0;
        }

        if (isLoggedIn) {
            bookingPayload.customerId = userData.user.userId;
            bookingPayload.customerEmail = userData.user.email;
            bookingPayload.customerName = `${userData.user.firstName} ${userData.user.lastName}`;
        } else {
            bookingPayload.customerName = customerName;
            bookingPayload.customerEmail = customerEmail;
        }

        if (mode === "reschedule" && existingBooking?.bookingId) {
            try {
                const token = isLoggedIn ? userData?.cognitoTokens?.IdToken : null;

                await scheduleBasedRequest.rescheduleBooking(
                    existingBooking.bookingId,
                    {
                        newStartTime: startLocal.tz("America/Santo_Domingo").format("YYYY-MM-DDTHH:mm:ss"),
                        newEndTime: endLocal.tz("America/Santo_Domingo").format("YYYY-MM-DDTHH:mm:ss"),
                        newStaffId: selectedstaff || existingBooking.staffId,
                        newStaffName: staffName || existingBooking.staffName,
                        newServices: selectedServices.map((s) => ({
                            serviceId: s.serviceId,
                            serviceNameEn: s.nameEn || s.name,
                            duration: s.duration,
                        })),
                    },
                    token
                );

                setToast("Booking rescheduled successfully!");
                navigate(`/booking/${existingBooking.bookingId}`);
                setTimeout(() => onClose?.(), 1000);
            } catch (err) {
                console.error("💥 Error rescheduling:", err);
                setValidationError(
                    language === "es" ? "No se pudo reprogramar la cita" : "Could not reschedule booking"
                );
                setConfirmLoading(false);
            }
            return;
        }

        try {
            const result = await scheduleBasedRequest.createBooking(bookingPayload);
            setToast("Booking confirmed! Email sent.");
            navigate(`/booking/${result.booking.bookingId}`);
        } catch (err) {
            console.error(err);
            setToast("Could not confirm booking");
            setValidationError(language === "es" ? "No se pudo confirmar la reserva." : "Could not confirm booking.");
            setConfirmLoading(false);
        }
    }

    function parseLocalDate(str) {
        const [y, m, d] = str.split("-").map(Number);
        return dayjs.tz(`${str} 12:00`, "America/Santo_Domingo").toDate();
    }

    function totalServicesText(n) {
        const t = translations[language].booking.summary;

        if (n === 0) return t.noServices;
        if (n === 1) return t.oneService;
        return t.multipleServices.replace("{n}", n);
    }

    function formatTime24To12(time24, language = "en") {
        if (!time24) return "";

        const [h, m] = time24.split(":").map(Number);
        const hour12 = h % 12 || 12;
        const minutes = m.toString().padStart(2, "0");
        const period = h >= 12 ? "PM" : "AM";

        return `${hour12}:${minutes} ${period}`;
    }

    if (!open || loading) return <BookingModalSkeleton />;

    return (
		<div className="sf-book-overlay" onClick={onClose}>
			<div className="sf-book-modal" onClick={(e) => e.stopPropagation()}>
				{/* Header with only close icon */}
                

				<div className="sf-book-body">
                    <div className="sf-book-header">
						<button className="sf-back-btn" onClick={onClose}>
							<ArrowLeft size={20} />
						</button>
						<h2>
							{shopData.name}
						</h2>
						
					</div>
					<StaffDropdown
						staffList={dayAvailability
							.filter((s) => (s.slots || []).length > 0 && staffCanHandleAllServices(s))
							.map((s) => ({ ...s, slotsCount: (s.slots || []).length }))}
						selectedStaffId={selectedstaff}
						onSelect={(id) => {
							setSelectedstaff(id);
							setStaffDropdownOpen(false);
						}}
						language={language}
						label={translations[language].booking.staff.title}
						open={staffDropdownOpen}
						onToggle={() => setStaffDropdownOpen((p) => !p)}
					/>

					{/* WEEK NAV */}
					<div className="sf-book-weeknav">
						<span className="sf-book-weeklabel">
							{dayjs(startDate).format("MMM D")} – {dayjs(startDate).add(6, "day").format("MMM D")}
						</span>
					</div>

					{/* DAYS */}
					<div className="sf-book-days-row">
						<button className="sf-book-navbtn" onClick={() => changeWeek(-1)}>
							<ChevronLeft />
						</button>
						<div className="sf-book-days">
							{Object.entries(timeData.week).map(([dateStr, d], i) => {
								const isSel = dayIndex === i;

								// Determine bar status
								let barStatus = "open"; // default
								if (d.status === "closed") barStatus = "closed";
								else if (d.status === "full" || d.totalSlots === 0) barStatus = "full";
								else if (d.totalSlots < 10) barStatus = "busy";

								return (
									<button
										key={dateStr}
										className={`sf-book-day ${isSel ? "is-active" : ""}`}
										onClick={() => {
											if (d.status !== "closed" && d.status !== "full") {
												setDayIndex(i);
												setDate(dateStr);
												setSelTime("");
											}
										}}
										disabled={d.status === "closed" || d.status === "full"}
									>
										<div className="sf-book-daytop">
											<span className="sf-day-name">
												{parseLocalDate(dateStr).toLocaleDateString(undefined, {
													weekday: "short",
												})}
											</span>
											<span className="sf-day-num">{parseLocalDate(dateStr).getDate()}</span>
										</div>

										{/* ✅ Availability bar with dynamic colors */}
										<div className={`sf-book-daybar ${barStatus}`}></div>
									</button>
								);
							})}
						</div>
						<button className="sf-book-navbtn" onClick={() => changeWeek(1)}>
							<ChevronRight />
						</button>
					</div>

					{/* TIME FILTERS */}
					<div className="sf-time-filters">
						{["Morning", "Afternoon", "Evening"].map((label) => {
							const isAvailable = availablePeriods.has(label);

							return (
								<button
									key={label}
									className={`sf-filter-segment ${timePeriod === label ? "active" : ""} ${!isAvailable ? "disabled" : ""}`}
									onClick={() => {
										if (!isAvailable) return; // prevent clicking unavailable filters
										setTimePeriod(label);
										if (!slotsRef.current) return;

										const ranges = {
											Morning: 0,
											Afternoon: 12,
											Evening: 17,
										};

										const targetBtn = Array.from(slotsRef.current.querySelectorAll(".sf-book-slot")).find((btn, index) => {
											const slot = slotsToShow[index];
											const hour = parseInt(slot.start.split(":")[0], 10);
											return hour >= ranges[label];
										});

										if (targetBtn) {
											targetBtn.scrollIntoView({
												behavior: "smooth",
												inline: "center",
												block: "nearest",
											});
										}
									}}
									disabled={!isAvailable}
								>
									{translations[language].booking.timeFilters[label.toLowerCase()]}
								</button>
							);
						})}
					</div>

					{/* SCROLLABLE TIME SLOTS */}
					<div className="sf-time-scroll">
						<button
							className={`sf-scroll-btn ${!canScrollLeft ? "disabled" : ""}`}
							onClick={() => {
								if (slotsRef.current) slotsRef.current.scrollBy({ left: -300, behavior: "smooth" });
							}}
						>
							<ChevronLeft />
						</button>

						<div
							className="sf-time-container"
							ref={slotsRef}
							onScroll={() => {
								if (!slotsRef.current) return;
								const { scrollLeft, scrollWidth, clientWidth } = slotsRef.current;
								setCanScrollLeft(scrollLeft > 0);
								setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
							}}
						>
							{slotsToShow.length > 0 ? (
								slotsToShow.map((slot) => (
									<button
										key={slot.start}
										className={`sf-book-slot ${selTime === slot.start ? "is-selected" : ""}`}
										onClick={() => setSelTime(slot.start)}
									>
										{formatTime24To12(slot.start, language)}
									</button>
								))
							) : (
								<div className="sf-noslots">{translations[language].booking.noSlots}</div>
							)}
						</div>

						<button
							className={`sf-scroll-btn ${!canScrollRight ? "disabled" : ""}`}
							onClick={() => {
								if (slotsRef.current) slotsRef.current.scrollBy({ left: 300, behavior: "smooth" });
							}}
						>
							<ChevronRight />
						</button>
					</div>

					{/* SERVICES SECTION */}
					<div className="sf-box">
						<h3>{translations[language].booking.services.title}</h3>
						{selectedServices.length > 0 && (
							<div className="sf-selected-services">
								{selectedServices.map((s) => (
									<div key={s.serviceId} className="sf-selected-service">
										<div>
											{s.name}
											{s.isHomeService && (
												<span className="sf-selected-service-home">
													<span className="sf-dot"> • </span>
													{language === "es" ? "A domicilio" : "Home service"}
												</span>
											)}
											<span className="sf-dot"> • </span>
											{s.duration} min <span className="sf-dot"> • </span> ${s.price}
										</div>
										<button onClick={() => setSelectedServices((p) => p.filter((srv) => srv.serviceId !== s.serviceId))}>✕</button>
									</div>
								))}
							</div>
						)}

						{filteredServices.length > selectedServices.length && (
							<select
								className="sf-book-input"
								onChange={(e) => {
									const serviceId = e.target.value;
									if (!serviceId) return;

									// Only allow services this staff can actually perform
									const s = filteredServices.find((srv) => String(srv.serviceId) === String(serviceId));

									if (!canAddService(s)) {
										setToast(
											language === "es"
												? "No puedes mezclar servicios a domicilio con servicios en el local."
												: "You can’t mix home services with in-shop services.",
										);
										e.target.value = "";
										return;
									}

									if (!s) {
										// Safety fallback (should never happen)
										console.warn("Service not allowed for this staff");
										e.target.value = "";
										return;
									}

									setSelectedServices((p) => [...p, s]);
									e.target.value = "";
								}}
								defaultValue=""
							>
								<option value="" disabled>
									{translations[language].booking.services.add}
								</option>

								{filteredServices
									.filter((s) => {
										if (s.isHomeService === true) return true;

										return !selectedServices.some((sel) => sel.serviceId === s.serviceId);
									})

									.map((s) => {
										const disabled = !canAddService(s);

										return (
											<option key={s.serviceId} value={s.serviceId} disabled={disabled}>
												{s.name}
												{s.isHomeService && <> • {language === "es" ? "A domicilio" : "Home service"}</>} • {s.duration} min • $
												{s.price}
												{disabled ? (language === "es" ? " (No combinable)" : " (Not mixable)") : ""}
											</option>
										);
									})}
								{lockedType && (
									<div className="sf-book-helper">
										{lockedType === "home"
											? language === "es"
												? "Solo puedes añadir servicios a domicilio en esta reserva."
												: "Only home services can be added to this booking."
											: language === "es"
												? "Solo puedes añadir servicios en el local en esta reserva."
												: "Only in-shop services can be added to this booking."}
									</div>
								)}
							</select>
						)}
					</div>

					{/* GUEST INFO */}
					{!isLoggedIn && !existingBooking && (
						<div className="sf-box">
							<h3>{translations[language].booking.guest.title}</h3>
							<input
								className="sf-book-input"
								type="text"
								placeholder={translations[language].booking.guest.fullName}
								value={customerName}
								onChange={(e) => setCustomerName(e.target.value)}
							/>
							<input
								className="sf-book-input"
								type="email"
								placeholder={translations[language].booking.guest.email}
								value={customerEmail}
								onChange={(e) => setCustomerEmail(e.target.value)}
							/>
							<PhoneInput businessPhone={homePhone} setBusinessPhone={setHomePhone} />
						</div>
					)}

					{isLoggedIn && !existingBooking && (
						<div className="sf-box">
                            <h3>{translations[language].booking.guest.title}</h3>
							<PhoneInput businessPhone={homePhone} setBusinessPhone={setHomePhone} />
						</div>
					)}
					{hasHomeService && !existingBooking && (
						<div className="sf-box">
							<h3>{language === "es" ? "Detalles del servicio a domicilio" : "Home service details"}</h3>

							{/* Address */}
							<RSFNewAutocomplete
								value={homeAddress.address}
								onChange={(val) => {
									setHomeAddress((prev) => ({
										...prev,
										address: val,
										lat: null,
										lng: null,
									}));
								}}
								onSelect={(data) => {
									setHomeAddress({
										address: data.address,
										city: data.city,
										country: data.country,
										lat: data.lat,
										lng: data.lng,
									});
								}}
								language={language}
							/>

							{/* Phone */}
							{/* <input
                                className="sf-book-input"
                                type="tel"
                                placeholder={
                                    language === "es" ? "Número de teléfono / WhatsApp" : "Phone / WhatsApp number"
                                }
                                value={homePhone}
                                onChange={(e) => setHomePhone(e.target.value)}
                            /> */}
						</div>
					)}
				</div>

				{validationError && <BannerMessageFixed type="error" text={validationError} />}

				<div className="sf-book-footer">
					<div className="sf-footer-summary">
						<span>{totalServicesText(selectedServices.length)}</span>
						<span>{totalTime ? `${totalTime} min` : ""}</span>
						<span>{totalPrice ? `$${totalPrice}` : ""}</span>
					</div>
					<button
						className={`sf-book-btn sf-book-btn--brand ${confirmLoading ? "is-loading" : ""}`}
						onClick={confirmBooking}
						disabled={confirmLoading}
					>
						{confirmLoading ? (
							<span className="sf-book-btn-spinner" />
						) : mode === "reschedule" ? (
							translations[language].booking.buttons.confirmReschedule
						) : (
							translations[language].booking.buttons.confirm
						)}
					</button>
				</div>

				{toast && <div className="sf-book-toast">{toast}</div>}
			</div>
		</div>
	);
};

export default BookingModal;
